
export class AudioService {
  private static instance: AudioService;
  private ctx: AudioContext | null = null;
  private nextStartTime: number = 0;

  private constructor() {}

  static getInstance(): AudioService {
    if (!AudioService.instance) {
      AudioService.instance = new AudioService();
    }
    return AudioService.instance;
  }

  getContext(): AudioContext {
    if (!this.ctx || this.ctx.state === 'closed') {
      this.ctx = new (window.AudioContext || (window as any).webkitAudioContext)({
        sampleRate: 24000
      });
    }
    if (this.ctx.state === 'suspended') {
      this.ctx.resume();
    }
    return this.ctx;
  }

  // Play raw PCM data from base64 string with optional playback rate
  async playPCM(base64Data: string, playbackRate: number = 1.0, sampleRate: number = 24000) {
    const ctx = this.getContext();
    const binaryString = atob(base64Data);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    const dataInt16 = new Int16Array(bytes.buffer);
    const buffer = ctx.createBuffer(1, dataInt16.length, sampleRate);
    const channelData = buffer.getChannelData(0);

    for (let i = 0; i < dataInt16.length; i++) {
      channelData[i] = dataInt16[i] / 32768.0;
    }

    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.playbackRate.value = playbackRate; // Apply speech speed
    source.connect(ctx.destination);
    
    // Schedule for gapless playback
    this.nextStartTime = Math.max(this.nextStartTime, ctx.currentTime);
    source.start(this.nextStartTime);
    
    // Adjust next start time based on actual duration at current speed
    this.nextStartTime += buffer.duration / playbackRate;
    
    return new Promise((resolve) => {
      source.onended = resolve;
    });
  }

  async playUrl(buffer: AudioBuffer) {
    const ctx = this.getContext();
    
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(ctx.destination);
    
    this.nextStartTime = Math.max(this.nextStartTime, ctx.currentTime);
    source.start(this.nextStartTime);
    this.nextStartTime += buffer.duration;
    
    return new Promise((resolve) => {
      source.onended = resolve;
    });
  }

  async playBlob(blob: Blob): Promise<void> {
    try {
      const arrayBuffer = await blob.arrayBuffer();
      const ctx = this.getContext();
      const audioBuffer = await ctx.decodeAudioData(arrayBuffer);
      
      await this.playUrl(audioBuffer);
      
    } catch (error) {
      console.error('Error playing audio from Blob:', error);
      throw error;
    }
  }

  async playAudioFromUrl(url: string): Promise<void> {
    try {
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`Failed to load audio: ${response.status}`);
      }
      
      const blob = await response.blob();
      await this.playBlob(blob);
      
    } catch (error) {
      console.error('Error playing audio from URL:', error);
      throw error;
    }
  }

  convertPCMToWav(base64Data: string, sampleRate: number = 24000): Blob {
    const binaryString = atob(base64Data);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    const wavHeader = new ArrayBuffer(44);
    const view = new DataView(wavHeader);

    const writeString = (view: DataView, offset: number, string: string) => {
      for (let i = 0; i < string.length; i++) {
        view.setUint8(offset + i, string.charCodeAt(i));
      }
    };

    writeString(view, 0, 'RIFF');
    view.setUint32(4, 36 + len, true);
    writeString(view, 8, 'WAVE');
    writeString(view, 12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, 1, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * 2, true);
    view.setUint16(32, 2, true);
    view.setUint16(34, 16, true);
    writeString(view, 36, 'data');
    view.setUint32(40, len, true);

    return new Blob([wavHeader, bytes], { type: 'audio/wav' });
  }

}
export const audioService = AudioService.getInstance();
